import { motion } from "framer-motion";
import { Database, DraftingCompass, ShoppingCart, ArrowUpRight, Check } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const CARD_LAYOUT = [
  {
    id: "gestionale",
    slug: "gestionali-su-misura",
    icon: Database,
    span: "md:col-span-7",
    glow: "hover:shadow-[0_0_50px_rgba(0,240,255,0.15)]",
    chipClass: "text-neon-cyan border-neon-cyan/40",
    img: "https://images.pexels.com/photos/27141316/pexels-photo-27141316.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "impianti",
    slug: "progettazione-impianti",
    icon: DraftingCompass,
    span: "md:col-span-5",
    glow: "hover:shadow-[0_0_50px_rgba(112,0,255,0.2)]",
    chipClass: "text-neon-violet border-neon-violet/40",
    img: "https://images.pexels.com/photos/13083354/pexels-photo-13083354.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "marketplace",
    slug: "automazione-marketplace",
    icon: ShoppingCart,
    span: "md:col-span-12",
    wide: true,
    glow: "hover:shadow-[0_0_50px_rgba(255,0,128,0.18)]",
    chipClass: "text-neon-magenta border-neon-magenta/40",
    img: "https://images.pexels.com/photos/12287907/pexels-photo-12287907.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

const Expertise = () => {
  const { t } = useLanguage();
  const CARDS = CARD_LAYOUT.map((c) => ({ ...c, chipLabel: t.expertise.cards[c.id].chip, title: t.expertise.cards[c.id].title, desc: t.expertise.cards[c.id].desc, bullets: t.expertise.cards[c.id].bullets }));
  return (
  <section id="servizi" className="py-24 lg:py-32" data-testid="expertise-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl mb-16"
      >
        <p className="text-xs font-bold tracking-[0.3em] uppercase text-neon-cyan mb-4">{t.expertise.overline}</p>
        <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
          {t.expertise.titleA} <span className="neon-text">{t.expertise.titleNeon}</span>
        </h2>
        <p className="mt-6 text-base lg:text-lg text-white/70">
          {t.expertise.subtitle}
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
        {CARDS.map((card, i) => (
          <motion.article
            key={card.id}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: i * 0.15 }}
            data-testid={`expertise-${card.id}-card`}
            className={`${card.span} glass-panel spotlight rounded-2xl overflow-hidden group hover:-translate-y-2 transition-all duration-500 ${card.glow} ${card.wide ? "md:flex" : ""}`}
          >
            <div className={card.wide ? "h-48 sm:h-56 md:h-auto md:w-2/5 shrink-0 overflow-hidden relative" : "h-48 sm:h-56 overflow-hidden relative"}>
              <img
                src={card.img}
                alt={`${card.chipLabel}: ${card.title} — XS Consulting`}
                className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#08080c] to-transparent" />
              <span className={`absolute top-4 left-4 text-xs font-bold tracking-widest uppercase border rounded-full px-3 py-1 bg-black/40 backdrop-blur ${card.chipClass}`}>
                {card.chipLabel}
              </span>
            </div>
            <div className="p-8 flex-1">
              <div className="flex items-center gap-3 mb-4">
                <card.icon size={26} className="text-neon-cyan" />
                <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{card.title}</h3>
              </div>
              <p className="text-white/70 leading-relaxed mb-6">{card.desc}</p>
              <ul className={`flex flex-col gap-3 mb-8 ${card.wide ? "md:grid md:grid-cols-2 md:gap-x-8" : ""}`}>
                {card.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-white/80">
                    <Check size={16} className="text-neon-cyan mt-0.5 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              <a
                href={`/servizi/${card.slug}`}
                data-testid={`expertise-${card.id}-cta`}
                className="inline-flex items-center gap-2 text-sm font-semibold text-neon-cyan hover:gap-3 transition-all duration-300"
              >
                {t.expertise.cta} <ArrowUpRight size={16} />
              </a>
            </div>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
  );
};

export default Expertise;
